import * as React from 'react';
import {INotificationServiceEventResponse, INotificationServiceEventsResponse, NotificationModel, NotificationsClientPlugin} from '@techsmith/notifications-client';
import Config from '../../globals/Config';
import Themeable from '../../types/Themeable';
import Strings from '../../globals/Strings';
import Log from '../../utils/Log';
import Constants, {NotificationTypes} from '../../globals/Constants';
import HttpService from '../../services/HttpService/HttpService';
import {TscStyles} from '@techsmith/tsc-cloud-style-guide';

const notificationContentText = Strings.notifications.content;

const baseUrl = Config.apiEndPoint + Config.apiPrefix;

const apiGetNotificationsOverride = async (_: string, __: string, cursor?: string, limit?: number): Promise<INotificationServiceEventsResponse> => {
   const queryString = `${cursor ? `&cursor=${cursor}` : ''}${limit ? `&limit=${limit}` : ''}`;
   const response = await HttpService.executeRequestWithErrorHandling(() => HttpService.getWithTscAuthToken(`${baseUrl}/users/me/notifications?cacheBuster=${(+new Date()).toFixed(0)}${queryString}`), 'apiGetNotificationsOverride', Constants.statusCodes.ok);
   return response.data as INotificationServiceEventsResponse;
};

const apiGetUnreadNotificationsCountOverride = async (): Promise<number> => {
   const response = await HttpService.executeRequestWithErrorHandling(() => HttpService.getWithTscAuthToken(`${baseUrl}/users/me/notifications/count?cacheBuster=${(+new Date()).toFixed(0)}`), 'apiGetUnreadNotificationsCountOverride', Constants.statusCodes.ok);
   return response.data;
};

const apiMarkAllAsReadOverride = async (): Promise<void> => {
   await HttpService.executeRequestWithErrorHandling(() => HttpService.putWithTscAuthToken(`${baseUrl}/users/me/notifications/read`), 'apiMarkAllAsReadOverride', Constants.statusCodes.noContent);
};

const apiMarkAsReadOverride = async (notificationId: string): Promise<void> => {
   await HttpService.executeRequestWithErrorHandling(() => HttpService.putWithTscAuthToken(`${baseUrl}/users/me/notifications/read/${notificationId}`), 'apiMarkAsReadOverride', Constants.statusCodes.noContent);
};

// cache these locally as lowercase for efficiency, will need to map new one if new notification types are added
const notificationTypesLower = {
   DeadlineApproaching: NotificationTypes.DeadlineApproaching.toLowerCase(),
   NewComment: NotificationTypes.NewComment.toLowerCase(),
   NewCommentReply: NotificationTypes.NewCommentReply.toLowerCase(),
   NewReviewConfirmation: NotificationTypes.NewReviewConfirmation.toLowerCase(),
   NewReviewInvitation: NotificationTypes.NewReviewInvitation.toLowerCase(),
   NewReviewVersionConfirmation: NotificationTypes.NewReviewVersionConfirmation.toLowerCase(),
   NewReviewVersionInvitation: NotificationTypes.NewReviewVersionInvitation.toLowerCase(),
   ReviewByDateModification: NotificationTypes.ReviewByDateModification.toLowerCase(),
   StartTrial: NotificationTypes.StartTrial.toLowerCase(),
   UserActivation: NotificationTypes.UserActivation.toLowerCase()
};

const notificationTranslationOverride = async (notificationResponse: INotificationServiceEventResponse): Promise<NotificationModel> => {
   const notification = notificationResponse as NotificationModel;
   if (notification.eventType.toLowerCase() === notificationTypesLower.NewReviewConfirmation) {
      notification.header = notificationContentText.newReviewConfirmation.header;
      notification.hideDisplayName = true;
   } else if (notification.eventType.toLowerCase() === notificationTypesLower.NewReviewInvitation) {
      notification.header = notificationContentText.newReviewInvitation.header;
      notification.body = notificationContentText.newReviewInvitation.body.replace('{param0}', notification.props.reviewTitle as string);
   } else if (notification.eventType.toLowerCase() === notificationTypesLower.DeadlineApproaching) {
      notification.header = notificationContentText.deadlineApproaching.header;
      notification.body = notificationContentText.newReviewInvitation.body.replace('{param0}', notification.props.reviewTitle as string).replace('{param1}', notification.props.reviewDeadline as string);
   } else if (notification.eventType.toLowerCase() === notificationTypesLower.ReviewByDateModification) {
      notification.header = notificationContentText.reviewByDateModification.header;
      notification.body = notificationContentText.newReviewInvitation.body.replace('{param0}', notification.props.reviewTitle as string).replace('{param1}', notification.props.reviewDeadline as string);
   } else if (notification.eventType.toLowerCase() === notificationTypesLower.NewReviewVersionConfirmation) {
      notification.header = notificationContentText.newReviewVersionConfirmation.header;
      notification.hideDisplayName = true;
   } else if (notification.eventType.toLowerCase() === notificationTypesLower.NewReviewVersionInvitation) {
      notification.header = notificationContentText.newReviewVersionInvitation.header;
      notification.body = notificationContentText.newReviewInvitation.body.replace('{param0}', notification.props.reviewTitle as string).replace('{param1}', notification.props.versionNumber as string);
   } else if (notification.eventType.toLowerCase() === notificationTypesLower.StartTrial) {
      notification.header = notificationContentText.startTrial.header;
      notification.hideDisplayName = true;
   } else if (notification.eventType.toLowerCase() === notificationTypesLower.NewComment) {
      notification.header = notificationContentText.newComment.header;
      notification.body = `"${notification.props.commentText}"`;
   } else if (notification.eventType.toLowerCase() === notificationTypesLower.NewCommentReply) {
      notification.header = notificationContentText.newCommentReply.header;
      notification.body = `"${notification.props.commentReplyText}"`;
   } else if (notification.eventType.toLowerCase() === notificationTypesLower.UserActivation) {
      notification.header = notificationContentText.userActivation.header;
      notification.url = '';
      notification.hideDisplayName = true;
   } else {
      Log.warn(`An unmapped notification of has been detected, masking from view, this is unexpected [${JSON.stringify(notificationResponse)}]`);
      notification.hideNotification = true;
      notification.header = notificationResponse.eventType;
      notification.body = `You received a message of unexpected type ${notificationResponse.eventType} with props ${JSON.stringify(notificationResponse.props)}`;
   }
   return Promise.resolve(notification);
};

export default ({themeName, currentUserId}: NotificationsClientPluginProps) => {
   return (
      <div className="notifications-button">
         <NotificationsClientPlugin
            themeName={themeName}
            serviceName="Feedback"
            currentUserId={currentUserId}
            palette={{
               unreadBackgroundGradientStartRgb: TscStyles.color.action.primary,
               unreadBackgroundGradientStopRgb: TscStyles.color.action.primary,
               unreadHighlightRgb: TscStyles.color.action.primary,
               unreadCountBackgroundRgba: `${TscStyles.color.action.primary}33`
            }}
            serverProxyConfig={{
               apiGetNotificationsOverride: apiGetNotificationsOverride,
               apiGetUnreadNotificationsCountOverride: apiGetUnreadNotificationsCountOverride,
               apiMarkAllAsReadOverride: apiMarkAllAsReadOverride,
               apiMarkAsReadOverride: apiMarkAsReadOverride
            }}
            notificationTranslation={notificationTranslationOverride}
         />
      </div>
   );
};

export interface NotificationsClientPluginProps extends Themeable {
   currentUserId: string;
}
